import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import LoginPage from './pages/LoginPage'
import { PwaInstallPrompt } from './components/PwaInstallPrompt'
import { resolvePreferredAccount } from './services/authSession'

// Lazy-load all authenticated pages — keeps the initial bundle small
// so the login page renders instantly on cold start
const Layout = lazy(() => import('./components/Layout'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const NewTicket = lazy(() => import('./pages/NewTicket'))
const TicketDetail = lazy(() => import('./pages/TicketDetail'))
const MyTickets = lazy(() => import('./pages/MyTickets'))
const ServiceCatalogue = lazy(() => import('./pages/ServiceCatalogue'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const AdminTicketDetail = lazy(() => import('./pages/AdminTicketDetail'))
const ApprovalAction = lazy(() => import('./pages/ApprovalAction'))
const MyApprovals = lazy(() => import('./pages/MyApprovals'))
const FinanceRequestAssetPurchase = lazy(() => import('./pages/FinanceRequestAssetPurchase'))
const FinanceLogApprovedPurchase = lazy(() => import('./pages/FinanceLogApprovedPurchase'))
const Finance = lazy(() => import('./pages/Finance'))
const FinanceAssetEntry = lazy(() => import('./pages/FinanceAssetEntry'))
const OrbitReleaseAudit = lazy(() => import('./pages/OrbitReleaseAudit'))
const Governance = lazy(() => import('./pages/Governance'))
const LegacyFDrive = lazy(() => import('./pages/LegacyFDrive'))
const NewStaffMember = lazy(() => import('./pages/NewStaffMember'))
const LaptopBuild = lazy(() => import('./pages/LaptopBuild'))
const StaffOffboarding = lazy(() => import('./pages/StaffOffboarding'))
const StaffLifecycle = lazy(() => import('./pages/StaffLifecycle'))

/**
 * Orbit ITSM - End User Portal
 * 
 * A white-label IT service management portal built by RME Solutions Technology.
 * 
 * END USERS can:
 * - Browse service catalogue
 * - Submit tickets (incidents and requests)
 * - View their ticket status
 * - Add comments to tickets
 * 
 * IT ADMINS can:
 * - Use /admin page to view all tickets and send update emails
 * - Use SharePoint directly for ticket management and triage
 * - Approvals workflow runs through the portal
 * 
 * NOTIFICATIONS:
 * - Teams webhook for new tickets + user comments → IT channel
 * - Frontend /me/sendMail for approval emails and admin update emails
 * - 1 Power Automate flow for inbound email → SharePoint comment
 * 
 * ACCESS CONTROL:
 * - Entra app registration: single-tenant (ICG only)
 * - Enterprise app: assignment required → ICG-All-Staff group
 * - Cloudflare Access: zero-trust gate on support.icgwagga.com.au
 */

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-icg-primary/30 border-t-icg-primary rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    </div>
  )
}

function App() {
  const { instance, accounts } = useMsal()
  const isAuthenticated = !!resolvePreferredAccount(instance, accounts)

  if (!isAuthenticated) {
    return (
      <>
        <PwaInstallPrompt />
        <LoginPage />
      </>
    )
  }

  return (
    <>
      <PwaInstallPrompt />
      <Suspense fallback={<LoadingFallback />}>
        <Layout>
          <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/services" element={<ServiceCatalogue />} />
          <Route path="/new" element={<NewTicket />} />
          <Route path="/new-ticket" element={<NewTicket />} />
          <Route path="/tickets" element={<MyTickets />} />
          <Route path="/ticket/:id" element={<TicketDetail />} />
          <Route path="/approve" element={<ApprovalAction />} />
          <Route path="/approvals" element={<MyApprovals />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/ticket/:id" element={<AdminTicketDetail />} />
          <Route path="/finance" element={<Finance />} />
          <Route path="/finance/request-purchase" element={<FinanceRequestAssetPurchase />} />
          <Route path="/finance/log-approved-purchase" element={<FinanceLogApprovedPurchase />} />
          <Route path="/finance/asset-entry" element={<FinanceAssetEntry />} />
          <Route path="/orbit-trace" element={<OrbitReleaseAudit />} />
          <Route path="/governance" element={<Governance />} />
          <Route path="/legacyFdrive" element={<LegacyFDrive />} />
          <Route path="/special/new-staff-member" element={<NewStaffMember />} />
          <Route path="/special/laptop-build" element={<LaptopBuild />} />
          <Route path="/special/offboarding" element={<StaffOffboarding />} />
          <Route path="/lifecycle" element={<StaffLifecycle />} />
        </Routes>
      </Layout>
      </Suspense>
    </>
  )
}

export default App
